import {
  Checkbox,
  CheckboxCard,
  Divider,
  Eyebrow,
  ListRow,
  Text,
  TextField,
  useEditLock,
} from "@/components/ui";
import { FieldSet, FormField } from "@/components/ui/Form";
import type { CrossModuleAssessmentParticipation, Module, ModuleAssessment } from "@/types";
import { type LedgerEntry, worstEntry } from "@/utils/assessment-weight";

/** Rounded for display; weightings are entered in whole or hundredth percents. */
function percent(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * What a participating module has left for this assessment, the tightest
 * version deciding, and the version it is over the limit in, if any.
 */
function availability(
  entries: readonly LedgerEntry[],
  coordinatingModuleId: string,
  assessmentId: string,
): { available: number; over?: LedgerEntry } | undefined {
  if (entries.length === 0) {
    return undefined;
  }
  const available = Math.min(
    ...entries.map(
      (entry) =>
        entry.headroom +
        entry.incoming
          .filter(
            (incoming) =>
              incoming.coordinatingModuleId === coordinatingModuleId &&
              incoming.assessmentId === assessmentId,
          )
          .reduce((sum, incoming) => sum + incoming.weighting, 0),
    ),
  );
  const over = worstEntry(entries.filter((entry) => !entry.withinLimit));
  return { available: Math.max(0, percent(available)), ...(over ? { over } : {}) };
}

function overLimit(module: Module, entry: LedgerEntry): string {
  const name = module.title || module.code || "the module";
  const where = entry.version ? ` in ${entry.version.label || entry.version.code}` : "";
  return `This takes ${name} to ${percent(entry.total)}%${where}. The limit is 100%.`;
}

interface CrossModuleAssessmentControlsProps {
  assessment: ModuleAssessment;
  coordinatingModuleId: string;
  modules: Module[];
  disabled?: boolean;
  /** Why every control here refuses, when it does. */
  disabledReason?: string;
  testIdPrefix: string;
  /** The assessment weight ledger's entries for a module, the draft included. */
  weightOf?: (moduleId: string) => readonly LedgerEntry[];
  onChange: (participatingModules: CrossModuleAssessmentParticipation[]) => void;
}

export function CrossModuleAssessmentControls({
  assessment,
  coordinatingModuleId,
  modules,
  disabled = false,
  disabledReason = undefined,
  testIdPrefix,
  weightOf = undefined,
  onChange,
}: CrossModuleAssessmentControlsProps) {
  const lock = useEditLock();
  const participatingModules = assessment.participatingModules ?? [];
  const availableModules = modules.filter((module) => module.id !== coordinatingModuleId);
  const enabled = participatingModules.length > 0;

  const toggleEnabled = (checked: boolean): void => {
    if (!checked) {
      onChange([]);
      return;
    }
    const firstModule = availableModules[0];
    onChange(firstModule ? [{ moduleId: firstModule.id, weighting: 0, mimloIds: [] }] : []);
  };

  const toggleModule = (moduleId: string, checked: boolean): void => {
    onChange(
      checked
        ? [...participatingModules, { moduleId, weighting: 0, mimloIds: [] }]
        : participatingModules.filter((participation) => participation.moduleId !== moduleId),
    );
  };

  const updateParticipation = (
    moduleId: string,
    update: (participation: CrossModuleAssessmentParticipation) => void,
  ): void => {
    onChange(
      participatingModules.map((participation) => {
        if (participation.moduleId !== moduleId) {
          return participation;
        }
        const next = { ...participation, mimloIds: [...participation.mimloIds] };
        update(next);
        return next;
      }),
    );
  };

  return (
    <>
      <Divider className="mt-5" />
      <FieldSet legend={<Eyebrow as="span">Cross-module assessment</Eyebrow>} className="pt-4">
        <CheckboxCard
          id={`${testIdPrefix}-enabled`}
          className="mt-2"
          label="This assessment covers other modules"
          description="This module coordinates one shared assessment. Add each participating module and record how much the assessment contributes to that module."
          checked={enabled}
          onChange={(next) => toggleEnabled(next)}
          disabled={disabled || availableModules.length === 0}
          // Under the lock its reason is the one that holds: with another
          // module to share with, the card still could not be ticked.
          disabledReason={
            lock.locked
              ? lock.reason
              : (disabledReason ?? "The programme has no other module to share it with.")
          }
          data-testid={`${testIdPrefix}-enabled`}
        />

        {enabled ? (
          <div className="mt-3 grid gap-3">
            {availableModules.map((module) => {
              const participation = participatingModules.find(
                (candidate) => candidate.moduleId === module.id,
              );
              const selected = participation != null;
              const weight = weightOf
                ? availability(weightOf(module.id), coordinatingModuleId, assessment.id)
                : undefined;
              const availableId = `${testIdPrefix}-module-${module.id}-available`;
              return (
                <ListRow key={module.id} data-testid={`${testIdPrefix}-module-${module.id}`}>
                  <Checkbox
                    size="sm"
                    checked={selected}
                    onChange={(next) => toggleModule(module.id, next)}
                    label={
                      <span className="font-semibold">
                        {module.code ? `${module.code} - ` : ""}
                        {module.title || "Untitled module"}
                      </span>
                    }
                    disabled={disabled}
                    disabledReason={disabledReason}
                    data-testid={`${testIdPrefix}-module-${module.id}-selected`}
                  />
                  {participation ? (
                    <div className="mt-3 grid gap-3 lg:grid-cols-[12rem_1fr]">
                      <div>
                        <FormField
                          label="Contribution to module %"
                          htmlFor={`${testIdPrefix}-module-${module.id}-weighting`}
                          error={weight?.over ? overLimit(module, weight.over) : undefined}
                        >
                          <TextField
                            id={`${testIdPrefix}-module-${module.id}-weighting`}
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            size="sm"
                            className="w-full"
                            value={participation.weighting}
                            onChange={(next) =>
                              updateParticipation(module.id, (draft) => {
                                draft.weighting = Math.min(100, Math.max(0, Number(next) || 0));
                              })
                            }
                            disabled={disabled}
                            disabledReason={disabledReason}
                            aria-describedby={weight ? availableId : undefined}
                            data-testid={`${testIdPrefix}-module-${module.id}-weighting`}
                          />
                        </FormField>
                        {weight ? (
                          <Text
                            tone="muted"
                            className="mt-1"
                            id={availableId}
                            data-testid={availableId}
                          >
                            {weight.available}% available
                          </Text>
                        ) : null}
                      </div>
                      <FieldSet legend="Measures module outcomes">
                        {(module.mimlos ?? []).length === 0 ? (
                          <Text tone="muted" className="mt-1">
                            This module has no MIMLOs to map yet.
                          </Text>
                        ) : (
                          <div className="mt-1 grid gap-2">
                            {(module.mimlos ?? []).map((mimlo, index) => (
                              <Checkbox
                                key={mimlo.id}
                                size="sm"
                                label={
                                  <>
                                    <strong>MIMLO {index + 1}:</strong> {mimlo.text}
                                  </>
                                }
                                checked={participation.mimloIds.includes(mimlo.id)}
                                onChange={(next) =>
                                  updateParticipation(module.id, (draft) => {
                                    draft.mimloIds = next
                                      ? Array.from(new Set([...draft.mimloIds, mimlo.id]))
                                      : draft.mimloIds.filter((id) => id !== mimlo.id);
                                  })
                                }
                                disabled={disabled}
                                disabledReason={disabledReason}
                                data-testid={`${testIdPrefix}-module-${module.id}-mimlo-${mimlo.id}`}
                              />
                            ))}
                          </div>
                        )}
                      </FieldSet>
                    </div>
                  ) : null}
                </ListRow>
              );
            })}
          </div>
        ) : null}
      </FieldSet>
    </>
  );
}
